import { logger } from '@/shared/utils/logger';
import { sanitizeUrlParams } from '@/shared/utils/input-validator';
import { getIpRestrictionAlertCallback } from '@/shared/utils/global-alert-manager';
import type { User } from '@/shared/types/userTypes';

export enum Environment {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
  TEST = 'test',
  DEMO = 'demo',
}

export const generateRequestId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
};

// Environment configuration with validation
const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key];
  if (!value && defaultValue === undefined) {
    logger.warn(`Environment variable ${key} is not set`, {}, 'ApiConfig');
  }
  return value || defaultValue || '';
};

export const envConfig = {
  apiHost: getEnvVar('VITE_API_HOST'),
  nodeEnv: getEnvVar('NODE_ENV', 'development'),
  appEnv: getEnvVar('VITE_APP_ENV', 'production'),
  googleMapsApiKey: getEnvVar('VITE_GOOGLE_MAPS_API_KEY'),
} as const;

// Validate required environment variables
const validateEnvConfig = () => {
  const requiredVars = ['VITE_API_HOST', 'VITE_APP_ENV'];
  const missingVars = requiredVars.filter(varName => !import.meta.env[varName]);

  if (missingVars.length > 0) {
    const error = `Missing required environment variables: ${missingVars.join(', ')}`;
    logger.error(error, { missingVars }, 'ApiConfig');
    throw new Error(error);
  }
};

// Validate on module load
validateEnvConfig();

export interface APIMessageResponse {
  message: string;
}

// Service error response
export interface ServiceErrorResponse {
  error: {
    status: number;
    code: string;
    message: string;
  }
}

function isServiceErrorResponse(data: any): data is ServiceErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof data.error === 'object' &&
    data.error !== null &&
    typeof data.error.message === 'string' &&
    typeof data.error.status === 'number'
  );
}

// API error response
export class ApiError extends Error {
  public status: number;
  public code: string;
  public message: string;
  public body?: ServiceErrorResponse;

  constructor(message: string, status: number, code: string, body?: ServiceErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.message = message;
    this.body = body;
  }
}

export interface ApiRequestConfig {
  params?: Record<string, any>;
  body?: any;
}

/**
 * 401 on these paths is a request failure (wrong password, guest /me, etc.),
 * not an expired in-app session to tear down via /logout.
 */
const UNAUTHORIZED_IGNORE_PATHS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/reset-password',
  '/api/v1/auth/validate-reset-token',
  '/api/v1/auth/send-otp',
  '/api/v1/auth/verify',
  '/api/v1/auth/logout',
]);

class ApiClient {
  public userId: string | null = null;

  private onUnauthorizedCallback: (() => void) | null = null;
  private isHandlingUnauthorized = false;
  /** True after a successful /me or login hydration; cleared on logout. */
  private sessionActive = false;

  public getEnv = (): string => {
    switch (envConfig.appEnv) {
      case Environment.PRODUCTION:
        return 'prod';
      case Environment.DEVELOPMENT:
        return 'dev';
      case Environment.TEST:
        return 'test';
      case Environment.DEMO:
        return 'demo';
    }
  };

  setAuthClaims(user: User): void {
    this.isHandlingUnauthorized = false;
    this.userId = user.userId || user.claims?.sub || null;
    this.sessionActive = true;
  }

  clearAuth(): void {
    this.userId = null;
    this.sessionActive = false;
  }

  // Method to register unauthorized callback
  setOnUnauthorizedCallback(callback: () => void): void {
    this.onUnauthorizedCallback = callback;
  }

  private logApiResponse(method: string, url: string, response: Response, startTime: number, requestId: string) {
    const duration = Date.now() - startTime;
    const responseHeaders = Object.fromEntries(response.headers.entries());
    logger.logApiResponse(method, url, response.status, duration, responseHeaders, response.body, requestId);
  }

  private logApiError(method: string, url: string, error: any, startTime: number, requestId: string) {
    const duration = Date.now() - startTime;
    logger.logApiError(method, url, error, duration, requestId);
  }

  private async request<T>(
    method: string,
    path: string,
    config: ApiRequestConfig = {}
  ): Promise<T> {
    const { params, body } = config;
    const requestId = generateRequestId();
    const startTime = Date.now();

    // Build the base URL
    const url = new URL(`${envConfig.apiHost}${path}`);

    try {
      if (params) {
        // Sanitize and validate URL parameters to prevent injection attacks
        const sanitizedParams = sanitizeUrlParams(params);
        Object.entries(sanitizedParams).forEach(([key, value]) => {
          url.searchParams.append(key, value);
        });
      }

      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'env': this.getEnv(),
      };

      logger.logApiRequest(method, url.toString(), headers, body, requestId);

      const response = await fetch(url.toString(), {
        method,
        headers,
        credentials: 'include',
        ...(body && { body: JSON.stringify(body) }),
      });

      if (!response.ok) {
        // Session teardown only for authenticated in-app calls — not guest /me or login.
        if (response.status === 401) {
          logger.warn('Received 401 error', { method, path, url: url.toString() }, 'ApiClient');

          const shouldClearSession =
            this.sessionActive &&
            !UNAUTHORIZED_IGNORE_PATHS.has(path) &&
            Boolean(this.onUnauthorizedCallback) &&
            !this.isHandlingUnauthorized;

          if (shouldClearSession) {
            this.isHandlingUnauthorized = true;
            this.onUnauthorizedCallback!();
          }

          // Read error response
          try {
            const raw = await response.json();
            const errorData: ServiceErrorResponse | null = isServiceErrorResponse(raw) ? raw : null;
            // Use error message from response if available, otherwise use default message
            const errorMessage = errorData?.error?.message || 'Session expired. Please log in again.';
            const errorCode = errorData?.error?.code || '';

            // Handle IP_RESTRICTION error - show prominent alert dialog
            if (errorCode === 'IP_RESTRICTION' && errorData?.error?.message) {
              const callback = getIpRestrictionAlertCallback();
              if (callback) {
                callback(errorData.error.message);
              }
            }

            throw new ApiError(errorMessage, response.status, errorCode, errorData);
          } catch (parseError) {
            if (parseError instanceof ApiError) throw parseError;
            throw new ApiError((parseError as Error).message || 'Session expired. Please log in again.', response.status, 'SESSION_EXPIRED');
          }
        }

        // For non-401 errors or non-auth-required requests, handle normally
        const raw = await response.json();
        const errorData: ServiceErrorResponse | null = isServiceErrorResponse(raw) ? raw : null;
        // Use error message from response if available, otherwise use generic message
        const errorMessage = errorData?.error?.message || 'API request failed';
        const errorCode = errorData?.error?.code || '';

        // Handle IP_RESTRICTION error - show prominent alert dialog
        if (errorCode === 'IP_RESTRICTION' && errorData?.error?.message) {
          const callback = getIpRestrictionAlertCallback();
          if (callback) {
            callback(errorData.error.message);
          }
        }

        throw new ApiError(errorMessage, response.status, errorCode, errorData);
      }

      if (response.status === 204) {
        this.logApiResponse(method, url.toString(), response, startTime, requestId);
        return undefined as T;
      }

      const data = await response.json();
      this.logApiResponse(method, url.toString(), response, startTime, requestId);

      return data;
    } catch (error) {
      this.logApiError(method, url.toString(), error, startTime, requestId);
      if (error instanceof ApiError) throw error;
      const err = error as Error;
      const status = typeof (error as { status?: number }).status === 'number' ? (error as { status: number }).status : 0;
      const errorData: ServiceErrorResponse = {
        error: {
          status: status || 500,
          message: err?.message || 'Network or unexpected error',
          code: (error as { code?: string })?.code || 'REQUEST_FAILED',
        },
      };
      throw new ApiError(err?.message || 'API request failed', status || 500, errorData.error.code, errorData);
    }
  }

  async get<T>(path: string, config: ApiRequestConfig = {}): Promise<T> {
    return this.request<T>('GET', path, config);
  }

  async post<T>(path: string, config: ApiRequestConfig = {}): Promise<T> {
    return this.request<T>('POST', path, config);
  }

  async put<T>(path: string, config: ApiRequestConfig = {}): Promise<T> {
    return this.request<T>('PUT', path, config);
  }

  async delete<T>(path: string, config: ApiRequestConfig = {}): Promise<T> {
    return this.request<T>('DELETE', path, config);
  }
}

export const apiClient = new ApiClient();
